import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';

import useHandleAddDiscount from '../../components/Tile/hooks/handlers/useHandleAddDiscount';

type AddDiscountDialogProps = {
  addDiscountDialogState: ReturnType<typeof useHandleAddDiscount>;
  loading?: boolean;
  errors?: string[];
  onClose: () => void;
  proceed: (discount: string) => void;
};

const AddDiscountDialog: FC<AddDiscountDialogProps> = ({
  addDiscountDialogState,
  loading,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['common']);

  const { discount, validationErrors, setDiscount, onValidate } =
    addDiscountDialogState;

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: 'Add a discount',
        content:
          errors && errors.length ? (
            <Stack sx={{ py: 1 }}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Stack sx={{ py: 1, gap: 2 }}>
              <TextField
                label='Discount'
                placeholder='Provide a discount ...'
                value={discount}
                error={!!validationErrors.discount}
                helperText={validationErrors.discount}
                inputProps={{ type: 'number', min: 0, max: 99 }}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </Stack>
          ),
        actions: [
          {
            variant: 'text',
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            variant: 'contained',
            label: t('common:proceed'),
            icon: loading && (
              <CircularProgress
                size='small'
                sx={{ color: '#fff', width: 16, height: 16, mr: 1 }}
              />
            ),
            onClick: () => {
              const hasValidationErrors = onValidate();

              if (!loading && !hasValidationErrors) {
                return proceed(discount);
              }
            },
          },
        ],
      }}
    />
  );
};

export default AddDiscountDialog;
